import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, Spin, Typography } from 'antd'
import keycloakAuth from '../services/keycloakAuth'
import authService from '../services/authService'
import { setCredentials } from '../store/slices/authSlice'
import './RedirectScreen.css'

const { Title, Text } = Typography

const OAuthCallback = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [error, setError] = useState(null)
  const [isTermsDeclined, setIsTermsDeclined] = useState(false)

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code')
      const state = searchParams.get('state')
      const errorParam = searchParams.get('error')
      const errorDescription = searchParams.get('error_description')

      if (errorParam) {
        const normalizedDescription = (errorDescription ?? '').replace(/\+/g, ' ').trim()
        const decodedDescription = normalizedDescription ? decodeURIComponent(normalizedDescription) : ''
        const loweredDescription = decodedDescription.toLowerCase()
        const normalizedError = errorParam.trim().toLowerCase()
        const declinedTerms =
          normalizedError === 'access_denied' && loweredDescription === 'termsandconditionsdeclined'

        const friendlyMessage = declinedTerms
          ? 'You must accept the Terms of Use & Privacy Policy to continue using Quantum Husky.'
          : decodedDescription || `Authentication failed (${errorParam}).`

        setError(friendlyMessage)
        setIsTermsDeclined(declinedTerms)
        setTimeout(() => navigate('/login', { replace: true }), 2500)
        return
      }

      if (!code || !state) {
        setError('Missing authorization response parameters')
        setTimeout(() => navigate('/login', { replace: true }), 2000)
        return
      }

      try {
        const tokens = await keycloakAuth.handleCallback(code, state)
        authService.setSession({
          token: tokens.access_token,
          refreshToken: tokens.refresh_token,
          idToken: tokens.id_token,
        })
        keycloakAuth.clearLogoutIntent()
        const user = await authService.getCurrentUser()

        dispatch(
          setCredentials({
            token: tokens.access_token,
            refreshToken: tokens.refresh_token,
            idToken: tokens.id_token,
            user,
          })
        )
        navigate('/dashboard', { replace: true })
      } catch (err) {
        console.error('OAuth callback error', err)

        // Handle Invalid OAuth state error gracefully - could be from password reset or expired session
        if (err.message === 'Invalid OAuth state' || err.message === 'Missing PKCE code verifier') {
          console.warn('OAuth state mismatch, likely from password reset or expired session. Redirecting to fresh login...')
          // Clear any stale session data
          sessionStorage.clear()
          navigate('/login', { replace: true })
          return
        }

        setError(err.message || 'Authentication failed')
        setIsTermsDeclined(false)
        setTimeout(() => navigate('/login', { replace: true }), 2500)
      }
    }

    processCallback()
  }, [searchParams, dispatch, navigate])

  const isError = Boolean(error)
  const heading = isError
    ? isTermsDeclined
      ? 'Terms of Use & Privacy Policy not accepted'
      : 'Authentication failed'
    : 'You are being signed in'
  const caption = isError
    ? isTermsDeclined
      ? 'Returning to login so you can accept and continue.'
      : 'Sending you back to login so you can try again.'
    : 'Redirecting you to the dashboard...'

  return (
    <div className="redirect-screen">
      <Card className="redirect-card" bodyStyle={{ padding: 40 }} bordered={false}>
        <div className="redirect-card__icon">
          <img src="/logo.png" alt="Quantum Husky" />
        </div>
        <Title level={isError ? 4 : 3} className="redirect-card__title">
          {heading}
        </Title>
        <Text type="secondary" className="redirect-card__hint">
          {caption}
        </Text>
        <div className="redirect-card__spinner">
          <Spin size="small" />
        </div>
        <div className="redirect-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </Card>
    </div>
  )
}

export default OAuthCallback
